import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { parseViewCtx, parseOverlaysUrl, type ViewCtx } from './contextUrl'
import { useBrandSpeciesSeed } from '../species/useBrandSpeciesSeed'
import { toggleFleet as toggleFleetPure, type FleetSegment } from '../models/fleetSegments'
import { DEFAULT_FRONT_UNIT, type FrontUnit } from '../maps/frontsMath'
import { toggleFlag, type FlagCount } from '../models/flagFilter'
import type { VesselFocus } from '../map/vesselFocus'
// Только тип: значения из map/mapView сюда не тянем — оттуда идёт обратная связь
// (useMapView читает этот контекст), и рантайм-импорт замкнул бы цикл модулей.
import type { MapView } from '../map/mapView'
import { parseIsobathStep } from '../map/isobathStep'
import { parseIsothermSpread } from '../map/isothermFamily'
import { seedOverlays } from './overlayDefaults'
import { useSpeciesScopeClamp } from '../species/useSpeciesScopeClamp'
import { clampDate } from '../domain/limits'
import { useDomainLimits } from '../domain/useDomainLimits'

const today = () => new Date().toISOString().slice(0, 10)

interface ViewContextValue extends ViewCtx {
  setDate: (d: string) => void
  setDepth: (d: number) => void
  setSpecies: (s: number | null) => void
  /** Применима ли глубина к активному слою/модели (SST/батиметрия/апвеллинг — нет).
   *  Ставит активный раздел; блок «Контекст» по нему гасит глубиномер. */
  depthApplicable: boolean
  setDepthApplicable: (v: boolean) => void
  /** Ключ каталога: ПОЧЕМУ глубина погашена (`null` — без пояснения). Ключ, а
   *  не текст: контекст про язык не знает (providerOrder.test.ts). */
  depthNote: string | null
  setDepthNote: (k: string | null) => void
  // ── Наложения: оверлеи + флот (общие для «Карт» и «Аналитики») ──
  overlays: string[]
  toggleOverlay: (id: string) => void
  fleet: FleetSegment[]
  toggleFleet: (seg: FleetSegment) => void
  /** «Показать суда без категорий» при выбранном виде (NPFC-фильтр). */
  showUncategorized: boolean
  setShowUncategorized: (v: boolean) => void
  /** Оверлей «Волнение»: высота волны красит стрелки течений (#0f172a→красный). */
  waveOverlay: boolean
  setWaveOverlay: (v: boolean) => void
  /** Шаг изолиний волнения, м (дефолт 0.5, ползунок 0.2–7). */
  waveStep: number
  setWaveStep: (v: number) => void
  /** Выбор флагов «Ин.флота»; пусто = все флаги (слой не гаснет). */
  flags: string[]
  toggleFlagFilter: (code: string) => void
  /** Флаги, найденные в текущем кадре, со счётчиками — из ответа слоя. */
  flagCounts: FlagCount[]
  setFlagCounts: (v: FlagCount[]) => void
  /** Раздел «Изобаты»: null = выключен, иначе шаг линий в метрах. */
  isobathStep: number | null
  setIsobathStep: (v: number | null) => void
  /** Память о регионе карты (центр+зум) между разделами: переход по рельсу уводит
   *  на голый путь без query, а вид жил только в query — и каждый раздел
   *  открывался на дефолтном центре. Держим в ref, а не в state: читается лишь при
   *  монтировании раздела, и ре-рендер всего кабинета на каждый пан не нужен. */
  recallMapView: () => MapView
  rememberMapView: (v: MapView) => void
  /** Судно, выбранное в строке поиска: карта летит к нему и открывает карточку.
   *  Живёт в контексте, потому что строка стоит в сайдбаре, а карта — в разделе. */
  focusVessel: VesselFocus | null
  setFocusVessel: (v: VesselFocus | null) => void
  // ── Инструмент «Изотермы + градиенты» (единый, фикс 5–16 °C, над любым слоем) ──
  isotherm: number | null // градус активной изотермы; null = выкл
  setIsotherm: (v: number | null) => void
  /** Градус поверхностной изотермы (на 0 м); null = выкл. Дочерняя к `isotherm`:
   *  её тумблер живёт внутри блока изотермы и исчезает вместе с ним. */
  isothermSurface: number | null
  setIsothermSurface: (v: number | null) => void
  /** Шаг смежных изотерм, °C; null = выкл. Тоже дочерний. */
  isothermSpread: number | null
  setIsothermSpread: (v: number | null) => void
  fronts: number | null // порог градиента (>0) в выбранной единице; null = выкл
  setFronts: (v: number | null) => void
  frontsUnit: FrontUnit // '100km' | '10km'
  setFrontsUnit: (u: FrontUnit) => void
}

const Ctx = createContext<ViewContextValue | null>(null)

/**
 * Общий контекст разделов: дата · глубина · вид. Живёт НАД маршрутами (в main.tsx,
 * внутри Router), поэтому не размонтируется при переходе Карты↔Аналитика — значения
 * сохраняются. Сид из URL — один раз при старте (шаринг/рефреш открывают тот же вид).
 *
 * В URL эти три ключа пишет САМА активная страница (её существующий сериализатор,
 * питаемый значениями контекста) — второго писателя мы не заводим, чтобы не спорить
 * за query со снимком prev у react-router (см. комментарий в ModelsPage про писателей).
 */
export function ViewProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams()
  const [seed] = useState(() => parseViewCtx(searchParams, today()))
  // Дата прижимается к окну ДОМЕНА на чтении, а не на записи: ссылка
  // `?date=` с прошлой неделей, сохранённый вид и бренд, приехавший позже
  // первого кадра, проходят через одну точку. На selara прошлого нет вовсе —
  // сервер ответил бы `date_forbidden` (решение владельца 20.10.2026).
  const limits = useDomainLimits()
  const [rawDate, setRawDate] = useState(seed.date)
  const date = clampDate(rawDate, today(), limits.period)
  const setDate = useCallback(
    (d: string) => setRawDate(clampDate(d, today(), limits.period)),
    [limits.period],
  )
  const [depth, setDepth] = useState(seed.depth)
  const [species, setSpecies] = useState<number | null>(seed.species)
  const [depthApplicable, setDepthApplicable] = useState(true)
  const [depthNote, setDepthNote] = useState<string | null>(null)
  // Вид чужого бассейна снимается сам: домен и права организации решают, каких
  // морей у этого кабинета нет вовсе, и ссылка `?species=26` на балтийском
  // домене не имеет права пережить ответ сервера. Пустой выбор после этого —
  // рабочее состояние, а не поломка (решение владельца 08.09.2026).
  useSpeciesScopeClamp(species, setSpecies)

  // Вид домена для холодного входа без вида в ссылке (SELARA — сардина).
  useBrandSpeciesSeed(species, setSpecies)

  // Наложения — сид из URL один раз (общий парс с сериализатором страниц, чтобы
  // рефреш/шаринг восстановили ровно включённые наложения); дальше живут в памяти
  // и общие для обоих разделов, а в URL их пишет активная страница.
  const [seedOv] = useState(() => parseOverlaysUrl(searchParams))
  // Землетрясения/луна/лёд включены при холодном входе (см. overlayDefaults):
  // именно СИД, а не значение по умолчанию в парсере, — иначе намеренно
  // выключенные наложения из ссылки оживали бы при каждом открытии.
  const [overlays, setOverlays] = useState<string[]>(() => seedOverlays(searchParams))
  const [fleet, setFleet] = useState<FleetSegment[]>(seedOv.fleet)
  const [showUncategorized, setShowUncategorized] = useState(seedOv.showUncat)
  const [waveOverlay, setWaveOverlay] = useState(seedOv.waves)
  const [waveStep, setWaveStep] = useState(seedOv.waveStep)
  const [flags, setFlags] = useState<string[]>(seedOv.flags)
  const [flagCounts, setFlagCounts] = useState<FlagCount[]>([])
  const [focusVessel, setFocusVessel] = useState<VesselFocus | null>(null)
  // Регион карты (центр+зум) — ref, а не state: его читают только при входе в
  // раздел, поэтому пан/зум не должен перерисовывать весь кабинет.
  const mapViewRef = useRef<MapView>({ lng: null, lat: null, zoom: null })
  const recallMapView = useCallback(() => mapViewRef.current, [])
  const rememberMapView = useCallback((v: MapView) => {
    mapViewRef.current = v
  }, [])
  const [isobathStep, setIsobathStep] = useState<number | null>(() => {
    const raw = searchParams.get('isobath')
    return raw == null || raw === '' ? null : parseIsobathStep(raw)
  })
  // Полный список флагов кадра нужен самому переключению: «выбраны все» —
  // это пустой фильтр, и первое нажатие должно СНЯТЬ страну со всего списка.
  const toggleFlagFilter = (code: string) =>
    setFlags(prev => toggleFlag(prev, code, flagCounts.map(f => f.code)))
  const toggleOverlay = (id: string) =>
    setOverlays(prev => (prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]))
  const toggleFleet = (seg: FleetSegment) => setFleet(prev => toggleFleetPure(prev, seg))

  // Инструмент температуры — сид из URL (iso/front/frontu), общий над разделами.
  const [isotherm, setIsotherm] = useState<number | null>(() => {
    const v = searchParams.get('iso')
    const n = v == null || v === '' ? NaN : Number(v)
    return Number.isFinite(n) ? n : null // 0° валиден
  })
  // Дочерние читаются из ссылки ТОЛЬКО при живой основной: `?isosurf=12` без
  // `iso` дал бы линию, которую нечем выключить (блок свёрнут).
  const [isothermSurface, setIsothermSurface] = useState<number | null>(() => {
    if (!searchParams.get('iso')) return null
    const v = searchParams.get('isosurf')
    const n = v == null || v === '' ? NaN : Number(v)
    return Number.isFinite(n) ? n : null // 0° валиден и здесь
  })
  const [isothermSpread, setIsothermSpread] = useState<number | null>(() => {
    if (!searchParams.get('iso')) return null
    const v = searchParams.get('isostep')
    return parseIsothermSpread(v == null || v === '' ? null : Number(v))
  })
  const [fronts, setFronts] = useState<number | null>(() => {
    const v = searchParams.get('front')
    const n = v == null || v === '' ? NaN : Number(v)
    return Number.isFinite(n) && n > 0 ? n : null
  })
  const [frontsUnit, setFrontsUnit] = useState<FrontUnit>(() =>
    searchParams.get('frontu') === '10km' ? '10km' : DEFAULT_FRONT_UNIT,
  )

  const value = useMemo(
    () => ({
      date,
      depth,
      species,
      setDate,
      setDepth,
      setSpecies,
      depthApplicable,
      setDepthApplicable,
      depthNote,
      setDepthNote,
      overlays,
      toggleOverlay,
      fleet,
      toggleFleet,
      showUncategorized,
      setShowUncategorized,
      waveOverlay,
      setWaveOverlay,
      waveStep,
      setWaveStep,
      flags,
      toggleFlagFilter,
      flagCounts,
      setFlagCounts,
      isobathStep,
      setIsobathStep,
      focusVessel,
      setFocusVessel,
      recallMapView,
      rememberMapView,
      isotherm,
      setIsotherm,
      isothermSurface,
      setIsothermSurface,
      isothermSpread,
      setIsothermSpread,
      fronts,
      setFronts,
      frontsUnit,
      setFrontsUnit,
    }),
    [
      date,
      setDate,
      depth,
      species,
      depthApplicable,
      depthNote,
      overlays,
      fleet,
      showUncategorized,
      waveOverlay,
      waveStep,
      flags,
      flagCounts,
      isobathStep,
      focusVessel,
      isotherm,
      isothermSurface,
      isothermSpread,
      fronts,
      frontsUnit,
    ],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useViewCtx(): ViewContextValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useViewCtx must be used within ViewProvider')
  return v
}
